import React, { useEffect, useState } from 'react';
import { useUserData } from '../../hooks/useUserData';
import { useContracts } from '../../hooks/useContract';
import { formatUsdc, bnToNumber } from '../../utils/format';
import { NetworkGuard } from '../wallet/NetworkGuard';
import { InlineLoader } from '../common/Loader';
import { Alert } from '../common/StatBox';

export const Team = () => (
  <NetworkGuard>
    <TeamInner />
  </NetworkGuard>
);

const TeamInner = () => {
  const { userId, isRegistered, basic, extended } = useUserData();
  const { viewsR } = useContracts();

  const [counts, setCounts] = useState(null);
  const [vols, setVols]     = useState(null);
  const [openLevel, setOpenLevel] = useState(null);
  const [levelMembers, setLevelMembers] = useState({});
  const [loadingLevel, setLoadingLevel] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!viewsR || !userId) return;
      try {
        const [c, v] = await Promise.all([
          viewsR.getUserLevelCounts(userId),
          viewsR.getUserLevelVolumes(userId)
        ]);
        if (cancelled) return;
        setCounts(c);
        setVols(v);
      } catch (e) {}
    };
    load();
    return () => { cancelled = true; };
  }, [viewsR, userId]);

  if (!isRegistered) return <Alert type="info">Register to build your team and start earning level income.</Alert>;

  const toggleLevel = async (levelIdx) => {
    if (openLevel === levelIdx) {
      setOpenLevel(null);
      return;
    }
    setOpenLevel(levelIdx);
    if (levelMembers[levelIdx]) return;
    if (!viewsR) return;

    setLoadingLevel(levelIdx);
    try {
      // fetch first 100 members of this level
      const res = await viewsR.getLevelUsers(userId, levelIdx, 0, 100);
      setLevelMembers((prev) => ({ ...prev, [levelIdx]: res[0] }));
    } catch (e) {
      setLevelMembers((prev) => ({ ...prev, [levelIdx]: [] }));
    } finally {
      setLoadingLevel(null);
    }
  };

  // Direct business = user's own totalInvestment (approximation for what direct-line business you brought in — same value shown on dashboard)
  // Team business = extended.teamVolume (all downline totalInvestment)
  const directCount = extended?.directReferrals?.toString() || '0';
  const teamCount   = extended?.activeMembersDownline?.toString() || '0';

  return (
    <div>
      {/* Top stats */}
      <div className="section-card">
        <div className="section-card-title">
          <span><span className="icon">👥</span> Team Overview</span>
        </div>
        <div className="stats-grid-compact" style={{ marginBottom: 0 }}>
          <div className="stat-mini cyan">
            <div className="stat-mini-label">Direct Count</div>
            <div className="stat-mini-value">{directCount}</div>
          </div>
          <div className="stat-mini pink">
            <div className="stat-mini-label">Team Count</div>
            <div className="stat-mini-value">{teamCount}</div>
          </div>
          <div className="stat-mini green">
            <div className="stat-mini-label">Direct Business</div>
            <div className="stat-mini-value">${formatUsdc(basic?.totalInvestment)}</div>
          </div>
          <div className="stat-mini gold">
            <div className="stat-mini-label">Team Business</div>
            <div className="stat-mini-value">${formatUsdc(extended?.teamVolume)}</div>
          </div>
        </div>
      </div>

      {/* Levels */}
      <div className="section-card">
        <div className="section-card-title">
          <span><span className="icon">🌐</span> 10 Levels</span>
          <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>Tap to expand</span>
        </div>
        {!counts || !vols ? <InlineLoader /> : (
          <div>
            {Array.from({ length: 10 }).map((_, i) => {
              const memberCount = bnToNumber(counts[i]);
              const vol = vols[i];
              const isOpen = openLevel === i;
              const members = levelMembers[i];
              const isLoadingMembers = loadingLevel === i;

              return (
                <div key={i} className={`level-row ${isOpen ? 'open' : ''}`}>
                  <div className="level-row-header" onClick={() => toggleLevel(i)}>
                    <div className="level-badge">L{i + 1}</div>
                    <div className="level-row-info">
                      <div className="level-row-name">Level {i + 1}</div>
                      <div className="level-row-meta">
                        {i === 0 ? '20% commission' : i <= 4 ? '10% commission' : '5% commission'}
                      </div>
                    </div>
                    <div className="level-row-stats">
                      <div className="level-row-count">{memberCount}</div>
                      <div className="level-row-vol">${formatUsdc(vol)}</div>
                    </div>
                    <span className="chev">▼</span>
                  </div>
                  {isOpen && (
                    <div className="level-row-body">
                      {isLoadingMembers ? (
                        <div style={{ textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 12 }}>
                          Loading members...
                        </div>
                      ) : !members || members.length === 0 ? (
                        <div style={{ textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 12, padding: '8px 0' }}>
                          No members at this level
                        </div>
                      ) : (
                        <>
                          <div className="level-member-list">
                            {members.slice(0, 100).map((uid, k) => (
                              <span key={k} className="level-member">#{uid.toString()}</span>
                            ))}
                          </div>
                          {members.length >= 100 && (
                            <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-tertiary)', textAlign: 'center' }}>
                              Showing first 100 members
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
